import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import { OrdersTable } from "@/components/orders/orders-table";
import { OrderFilters } from "@/components/orders/order-filters";
import { Button } from "@/components/ui/button";
import { IconDownload, IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/lib/constants";
import { DataExportDialog } from "@/components/shared/data-export-dialog";
import { useExportOrders } from "@/hooks/use-data-export";
import { toast } from "sonner";

export const Route = createFileRoute("/orders/")({
  component: OrdersPage,
});

function OrdersPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <OrdersContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function OrdersContent() {
  const [filters, setFilters] = useState({
    status: "all",
    dateRange: "today",
    merchant: "all",
    search: "",
  });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const exportOrders = useExportOrders();

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS });
    toast.success("Orders Refreshed", {
      description: "The orders list has been updated with the latest data.",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Orders</h1>
          <p className="text-muted-foreground">
            Manage and track all payment orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <IconRefresh className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExportDialogOpen(true)}
          >
            <IconDownload className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      <OrderFilters filters={filters} onFiltersChange={setFilters} />
      <OrdersTable filters={filters} />

      <DataExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        title="Export Orders"
        description="Export order data in your preferred format"
        onExport={async (params) => exportOrders.mutateAsync(params) as unknown as Promise<Blob>}
        filename="orders"
      />
    </div>
  );
}
